import { flattenProductServicesForDropdown } from "./productServiceItemDropdown";

export function walkUpstreamActionNodes(nodes, edges, currentNodeId, onNode) {
  if (!nodes?.length || !edges?.length || !currentNodeId) return;

  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set();
  const queue = [currentNodeId];

  while (queue.length) {
    const cur = queue.shift();
    if (!cur || visited.has(cur)) continue;
    visited.add(cur);

    const incomingEdges = edges.filter((e) => e?.target === cur);
    incomingEdges.forEach((edge) => {
      const sourceId = edge?.source;
      const sourceNode = nodesById.get(sourceId);
      if (sourceNode) {
        onNode(sourceNode);
      }
      queue.push(sourceId);
    });
  }
}

/**
 * Product/service rows added by upstream add_item actions (ancestors in the flow).
 */
export function getUpstreamAddedProductServices(nodes, edges, currentNodeId) {
  const added = [];
  const seenIds = new Set();

  const appendProductService = (ps) => {
    if (!ps?.document_item_product_service_id) return;
    const id = String(ps.document_item_product_service_id);
    if (seenIds.has(id)) return;
    seenIds.add(id);
    added.push({
      ...ps,
      is_inside_bundle: Boolean(ps.is_inside_bundle),
      is_bundle: Boolean(ps.is_bundle)
    });
  };

  walkUpstreamActionNodes(nodes, edges, currentNodeId, (sourceNode) => {
    if (sourceNode.type !== "actionNode" || sourceNode.data?.nodeProps?.action !== "add_item") {
      return;
    }
    const ps = sourceNode.data?.nodeProps?.answers?.new_product_object;
    if (!ps) return;
    appendProductService(ps);
    (ps.children || []).forEach((child) => {
      appendProductService({
        ...child,
        is_inside_bundle: true,
        product_service_parent_id: [ps.document_item_product_service_id]
      });
    });
  });

  return added;
}

/**
 * Upstream add_item action nodes in approximate flow-execution order
 * (further ancestors first). BFS from the current node visits closer nodes first;
 * reversing that list yields earlier-to-later for linear flows.
 */
export function getUpstreamAddItemActionNodes(nodes, edges, currentNodeId) {
  const collected = [];
  walkUpstreamActionNodes(nodes, edges, currentNodeId, (sourceNode) => {
    if (sourceNode.type !== "actionNode" || sourceNode.data?.nodeProps?.action !== "add_item") {
      return;
    }
    collected.push(sourceNode);
  });
  collected.reverse();
  return collected;
}

/**
 * Maps upstream add_item root product service IDs to their wizard action node id.
 */
export function getUpstreamAddItemActionRootByProductServiceId(nodes, edges, currentNodeId) {
  const map = new Map();

  walkUpstreamActionNodes(nodes, edges, currentNodeId, (sourceNode) => {
    if (sourceNode.type !== "actionNode" || sourceNode.data?.nodeProps?.action !== "add_item") {
      return;
    }
    const actionId = sourceNode.data?.nodeProps?.id;
    const ps = sourceNode.data?.nodeProps?.answers?.new_product_object;
    if (actionId && ps?.document_item_product_service_id) {
      map.set(String(ps.document_item_product_service_id), String(actionId));
    }
  });

  return map;
}

/**
 * Collects template product service IDs marked for removal by upstream remove_item
 * action nodes (ancestors of the current node in the wizard flow graph).
 */
export function getUpstreamRemovedProductServiceIds(nodes, edges, currentNodeId) {
  const removed = new Set();

  walkUpstreamActionNodes(nodes, edges, currentNodeId, (sourceNode) => {
    if (sourceNode.type !== "actionNode" || sourceNode.data?.nodeProps?.action !== "remove_item") {
      return;
    }
    const { answers = {}, id: actionId } = sourceNode.data.nodeProps;
    const templatePsId = answers[`document_item_product_service_id_to_remove_${actionId}`];
    if (templatePsId != null && templatePsId !== "") {
      removed.add(String(templatePsId));
    }
  });

  return removed;
}

/**
 * Merges template product services with upstream add_item rows for dropdowns.
 */
export function mergeProductServicesForWizardDropdown(templateProductServices, upstreamAdded) {
  const templateIds = new Set(
    flattenProductServicesForDropdown(templateProductServices || []).map((ps) =>
      String(ps.document_item_product_service_id)
    )
  );

  const extra = (upstreamAdded || []).filter(
    (ps) => ps?.document_item_product_service_id && !templateIds.has(String(ps.document_item_product_service_id))
  );

  return [...(templateProductServices || []), ...extra];
}

/**
 * Expands removed template IDs to include all bundle children when a bundle parent is removed.
 */
export function expandRemovedProductServiceIds(productServices, removedTemplateIds) {
  const expanded = new Set([...removedTemplateIds].map(String));
  if (!expanded.size) return expanded;

  const flat = flattenProductServicesForDropdown(productServices);

  flat.forEach((ps) => {
    const id = String(ps.document_item_product_service_id);
    if (!expanded.has(id)) return;

    if (ps.is_bundle || ps.children?.length) {
      flat.forEach((candidate) => {
        if (candidate.is_inside_bundle && getParentId(candidate) === id) {
          expanded.add(String(candidate.document_item_product_service_id));
        }
      });
      (ps.children || []).forEach((child) => {
        if (child?.document_item_product_service_id) {
          expanded.add(String(child.document_item_product_service_id));
        }
      });
    }
  });

  return expanded;
}

function getParentId(ps) {
  const parent = ps?.product_service_parent_id;
  if (Array.isArray(parent)) return parent[0] != null ? String(parent[0]) : null;
  if (parent) return String(parent);
  return null;
}
